import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, Loader2, Sparkles, ShieldCheck, Check, Shuffle, RotateCcw, Wand2, Briefcase } from "lucide-react";
import { CvShell } from "@/components/cv/CvShell";
import { CvGate } from "@/components/cv/CvGate";
import { useAuth } from "@/context/AuthContext";
import { useCredits } from "@/context/CreditsContext";
import { cvApi } from "@/lib/api";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const A4_W = 794;
const A4_H = 1123;

const TONES = [
  { key: "formalny", label: "Formalny" },
  { key: "bezposredni", label: "Bezpośredni" },
];
const LENGTHS = [
  { key: "krotki", label: "Krótki" },
  { key: "standardowy", label: "Standardowy" },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-[13px] font-medium text-brand-text">{label}</span>
    {children}
  </label>
);

export default function CvLetterEditor() {
  const { letterId } = useParams();
  const { user } = useAuth();
  const { confirmAndRun, priceOf } = useCredits();
  const [letter, setLetter] = useState(null);
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [savingApp, setSavingApp] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!user) return;
    cvApi.letterGet(letterId).then(setLetter).catch(() => setError("Nie udało się wczytać listu."));
  }, [letterId, user]);

  const previewUrl = useMemo(() => `${BASE}/cv/letters/${letterId}/render?_=${nonce}`, [letterId, nonce]);
  useEffect(() => { setLoadingPreview(true); }, [previewUrl]);

  const pendingRef = useRef({});
  const flush = useCallback(async () => {
    const data = pendingRef.current;
    if (!data || Object.keys(data).length === 0) return;
    pendingRef.current = {};
    try { await cvApi.letterPatch(letterId, data); setSavedAt(new Date()); } catch { /* best-effort */ }
  }, [letterId]);

  const schedule = useCallback((patch, refreshPreview = false) => {
    pendingRef.current = { ...pendingRef.current, ...patch };
    setConfirmed(false);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      await flush();
      if (refreshPreview) setNonce((n) => n + 1);
    }, 700);
  }, [flush]);

  // Zapisz oczekujące zmiany przy opuszczeniu edytora.
  useEffect(() => () => { flush(); }, [flush]);

  const setField = useCallback((key, value) => {
    setLetter((prev) => ({ ...prev, [key]: value }));
    schedule({ [key]: value });
  }, [schedule]);

  const generate = async (variant = false) => {
    if (!letter?.company?.trim() || !letter?.role?.trim()) {
      toast.error("Podaj nazwę firmy i stanowisko przed generowaniem.");
      return;
    }
    setGenerating(true);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await flush();
      const actionKey = variant ? "cv_cover_letter_variant" : "cv_cover_letter";
      const d = await confirmAndRun(actionKey, () => cvApi.letterGenerate(letterId, variant));
      if (d && d.body !== undefined) {
        setLetter((prev) => ({ ...prev, body: d.body, versions: d.versions || prev.versions || [] }));
        setNonce((n) => n + 1);
        setConfirmed(false);
        toast.success(variant ? "Nowa wersja gotowa. Poprzednia jest w historii." : "List wygenerowany. Sprawdź i dopracuj treść.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const extractFromOffer = async () => {
    if (!letter?.job_offer?.trim()) {
      toast.error("Najpierw wklej treść oferty w polu poniżej.");
      return;
    }
    setExtracting(true);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await flush();
      const d = await cvApi.letterExtractOffer(letterId);
      setLetter((prev) => ({ ...prev, company: d.company || prev.company, role: d.role || prev.role }));
      toast.success("Uzupełniono firmę i stanowisko z oferty.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Nie udało się odczytać danych z oferty.";
      toast.error(typeof msg === "string" ? msg : "Nie udało się odczytać danych z oferty.");
    } finally {
      setExtracting(false);
    }
  };

  const restoreVersion = async (index) => {
    try {
      const d = await cvApi.letterRestore(letterId, index);
      setLetter((prev) => ({ ...prev, body: d.body, versions: d.versions || [] }));
      setNonce((n) => n + 1);
      setConfirmed(false);
      toast.success("Przywrócono wybraną wersję.");
    } catch {
      toast.error("Nie udało się przywrócić wersji.");
    }
  };

  const saveBody = (value) => {
    setLetter((prev) => ({ ...prev, body: value }));
    schedule({ body: value }, true);
  };

  const saveAsApplication = async () => {
    setSavingApp(true);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await flush();
      await cvApi.appCreate({
        cv_id: letter.cv_id || "", letter_id: letterId,
        company: letter.company || "", role: letter.role || "", job_offer: letter.job_offer || "",
      });
      toast.success("Zapisano jako aplikację. Znajdziesz ją w „Moje aplikacje”.");
    } catch {
      toast.error("Nie udało się zapisać aplikacji.");
    } finally {
      setSavingApp(false);
    }
  };

  const download = async (fmt) => {
    if (!confirmed) return;
    setDownloading(fmt);
    try {
      const blob = await cvApi.letterExportBlob(letterId, fmt);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `List_motywacyjny.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      await cvApi.letterPatch(letterId, { status: "ready" });
      toast.success(`Pobrano plik ${fmt.toUpperCase()}.`);
    } catch {
      toast.error("Nie udało się pobrać pliku. Spróbuj ponownie.");
    } finally {
      setDownloading("");
    }
  };

  const inputCls = "w-full rounded-button border border-brand-line bg-brand-bg px-3 py-2.5 text-sm text-brand-text focus:border-brand-accent focus:outline-none";

  return (
    <CvShell active="listy">
      <div data-testid="cv-letter-editor" className="pb-28">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-2xl font-semibold text-brand-text sm:text-3xl">Kreator listu motywacyjnego</h1>
          {savedAt && <span className="inline-flex items-center gap-1 text-[12.5px] text-brand-muted"><Check size={13} className="text-brand-accent" /> Zapisano roboczo</span>}
        </div>

        {!user ? (
          <div className="mt-8 max-w-2xl"><CvGate returnTo={`/cv/list/${letterId}`} message="Załóż bezpłatne konto, aby tworzyć listy motywacyjne." /></div>
        ) : error ? (
          <div className="mt-8 rounded-panel border border-red-500/40 bg-red-500/10 p-6 text-sm text-red-300">{error}</div>
        ) : !letter ? (
          <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-brand-accent" /></div>
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-[420px_1fr]">
            {/* Formularz */}
            <div className="space-y-4">
              <Link to="/cv/listy" data-testid="cv-letter-back" className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text hover:border-brand-accent/60">
                <ArrowLeft size={15} /> Wróć do listy
              </Link>

              <Field label="Firma / organizacja *">
                <input data-testid="cv-letter-company" className={inputCls} value={letter.company || ""} onChange={(e) => setField("company", e.target.value)} placeholder="np. Kancelaria Nowak Sp. z o.o." />
              </Field>
              <Field label="Stanowisko *">
                <input data-testid="cv-letter-role" className={inputCls} value={letter.role || ""} onChange={(e) => setField("role", e.target.value)} placeholder="np. Główna Księgowa" />
              </Field>
              <Field label="Adresat (opcjonalnie)">
                <input data-testid="cv-letter-recipient" className={inputCls} value={letter.recipient || ""} onChange={(e) => setField("recipient", e.target.value)} placeholder="np. Szanowna Pani Kowalska" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ton">
                  <select data-testid="cv-letter-tone" className={inputCls} value={letter.tone || "formalny"} onChange={(e) => setField("tone", e.target.value)}>
                    {TONES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </Field>
                <Field label="Długość">
                  <select data-testid="cv-letter-length" className={inputCls} value={letter.length || "standardowy"} onChange={(e) => setField("length", e.target.value)}>
                    {LENGTHS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Atuty do podkreślenia (opcjonalnie)">
                <textarea data-testid="cv-letter-highlights" rows={3} className={inputCls} value={letter.highlights || ""} onChange={(e) => setField("highlights", e.target.value)} placeholder="np. 8 lat w księgowości, wdrożenie e-obiegu faktur, zarządzanie zespołem" />
              </Field>
              <Field label="Treść ogłoszenia (opcjonalnie)">
                <textarea data-testid="cv-letter-offer" rows={3} className={inputCls} value={letter.job_offer || ""} onChange={(e) => setField("job_offer", e.target.value)} placeholder="Wklej fragment oferty, aby lepiej dopasować list" />
              </Field>
              {letter.job_offer?.trim() && (
                <button type="button" data-testid="cv-letter-extract" disabled={extracting} onClick={extractFromOffer} className="-mt-1 inline-flex items-center gap-2 rounded-button border border-brand-line px-3.5 py-2 text-[13px] font-semibold text-brand-text hover:border-brand-accent/60 disabled:opacity-50">
                  {extracting ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Uzupełnij firmę i stanowisko z oferty
                </button>
              )}

              <button type="button" data-testid="cv-letter-generate" disabled={generating} onClick={() => generate(false)} className="inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-50">
                {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {letter.body ? "Wygeneruj ponownie" : "Wygeneruj list (AI)"}{priceOf("cv_cover_letter") ? ` · ${priceOf("cv_cover_letter")} kr` : ""}
              </button>
              {letter.body && (
                <button type="button" data-testid="cv-letter-variant" disabled={generating} onClick={() => generate(true)} className="inline-flex w-full items-center justify-center gap-2 rounded-button border border-brand-line px-5 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60 disabled:opacity-50">
                  <Shuffle size={16} /> Inna wersja (inny styl){priceOf("cv_cover_letter_variant") ? ` · ${priceOf("cv_cover_letter_variant")} kr` : ""}
                </button>
              )}
              <p className="flex items-start gap-2 text-[12px] text-brand-muted"><ShieldCheck size={14} className="mt-0.5 shrink-0 text-brand-accent" /> AI porządkuje wyłącznie Twoje dane z CV i wskazówki — nie dodaje zmyślonych faktów. Sprawdź treść przed pobraniem.</p>

              {(letter.versions?.length > 0) && (
                <div className="rounded-panel border border-brand-line bg-brand-surface p-4" data-testid="cv-letter-versions">
                  <p className="mb-2 text-[13px] font-semibold text-brand-text">Poprzednie wersje ({letter.versions.length})</p>
                  <ul className="space-y-2">
                    {letter.versions.map((v, i) => (
                      <li key={i} className="flex items-start gap-2 rounded-card border border-brand-line bg-brand-bg p-2.5">
                        <span className="min-w-0 flex-1 text-[12px] text-brand-muted line-clamp-2">{(v.body || "").slice(0, 140)}…</span>
                        <button type="button" data-testid={`cv-letter-restore-${i}`} onClick={() => restoreVersion(i)} className="inline-flex shrink-0 items-center gap-1 rounded-button border border-brand-line px-2.5 py-1.5 text-[12px] font-semibold text-brand-text hover:border-brand-accent/60">
                          <RotateCcw size={12} /> Przywróć
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <Field label="Treść listu (możesz edytować)">
                <textarea data-testid="cv-letter-body" rows={12} className={inputCls} value={letter.body || ""} onChange={(e) => saveBody(e.target.value)} placeholder="Tu pojawi się wygenerowany list. Możesz go dowolnie edytować." />
              </Field>

              <label className="flex cursor-pointer items-start gap-2.5 rounded-panel border border-brand-line bg-brand-surface p-4">
                <input type="checkbox" data-testid="cv-letter-confirm" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[color:var(--brand-accent,#b6f500)]" />
                <span className="text-[13px] text-brand-text">Zatwierdzam treść — jest poprawna i zgodna z moim rzeczywistym doświadczeniem.</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="cv-letter-pdf" disabled={!confirmed || downloading || !letter.body} onClick={() => download("pdf")} className="inline-flex items-center justify-center gap-2 rounded-button bg-brand-accent px-4 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40">
                  {downloading === "pdf" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} PDF
                </button>
                <button type="button" data-testid="cv-letter-docx" disabled={!confirmed || downloading || !letter.body} onClick={() => download("docx")} className="inline-flex items-center justify-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60 disabled:cursor-not-allowed disabled:opacity-40">
                  {downloading === "docx" ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} DOCX
                </button>
              </div>
              <button type="button" data-testid="cv-letter-save-app" disabled={savingApp} onClick={saveAsApplication} className="inline-flex w-full items-center justify-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60 disabled:opacity-50">
                {savingApp ? <Loader2 size={16} className="animate-spin" /> : <Briefcase size={16} />} Zapisz jako aplikację
              </button>
            </div>

            {/* Podgląd A4 */}
            <div>
              <p className="mb-2 text-sm font-semibold text-brand-text">Podgląd A4</p>
              <div data-testid="cv-letter-preview" className="relative overflow-auto rounded-panel border border-brand-line bg-[#525659] p-4" style={{ maxHeight: "80vh" }}>
                {loadingPreview && <div className="absolute right-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/50 px-2.5 py-1 text-[11px] text-white"><Loader2 size={12} className="animate-spin" /> Odświeżanie…</div>}
                <div className="mx-auto shadow-xl" style={{ width: A4_W * 0.62, height: A4_H * 0.62 }}>
                  <iframe
                    key={previewUrl}
                    data-testid="cv-letter-iframe"
                    title="Podgląd listu A4"
                    src={previewUrl}
                    onLoad={() => setLoadingPreview(false)}
                    style={{ width: A4_W, height: A4_H, border: "none", background: "white", display: "block", transform: "scale(0.62)", transformOrigin: "top left" }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </CvShell>
  );
}
